export interface FlexField {
  name: string;
  label: string;
}

const EXCLUDED_FIELDS = [
  'report',
  'utilityBill',
  'damageReport',
  'dateOfLoss',
  'hailSeverity',
  'hailCount',
  'hailSize',
  'windSeverity',
  'recordType',
  'leadSource',
  'photos',
  'windSpeed',
];

const EXCLUDED_TYPES = ['WebView', 'Read Only', 'imageList'];

/**
 * Converts a camelCase string to a human-readable label.
 * e.g. "firstName" -> "First Name", "postalCode" -> "Postal Code"
 */
function camelCaseToLabel(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Finds the "Contacts" category from a list of stage categories.
 */
export function findContactsCategory(
  categories: Array<{ id: string; title: string; [key: string]: any }>
) {
  return (
    categories.find(
      (c) => (c.title ?? '').trim().toLowerCase() === 'contacts'
    ) ?? null
  );
}

/**
 * Extracts mappable flex fields from a category's parsed flexAttributes.
 * Navigates the nested config structure, finds displayFields + flexFields,
 * and filters out system/excluded/read-only fields.
 */
export function extractFlexFields(
  flexAttributes: Record<string, any> | null | undefined
): FlexField[] {
  if (!flexAttributes) return [];

  const dealConfig = flexAttributes?.config?.deal;
  if (!dealConfig) return [];

  const dealKeys = Object.keys(dealConfig);

  // Resolve the customer/contact deal type, or fall back to dealConfig itself
  const resolved =
    dealConfig.Customer ??
    dealConfig.customer ??
    dealConfig.Contact ??
    dealConfig.contact ??
    dealConfig[
      dealKeys.find((k) => k.toLowerCase().includes('customer')) ?? ''
    ] ??
    dealConfig[
      dealKeys.find((k) => k.toLowerCase().includes('contact')) ?? ''
    ] ??
    dealConfig;

  const displayFields: string[] =
    resolved?.displayFields ?? dealConfig?.displayFields ?? [];
  const flexFields: any[] =
    resolved?.flexFields ?? dealConfig?.flexFields ?? [];

  if (!displayFields.length && !flexFields.length) return [];

  const result: FlexField[] = [];

  for (const fieldName of displayFields) {
    if (EXCLUDED_FIELDS.includes(fieldName)) continue;

    const flex = flexFields.find((f: any) => f.name === fieldName);
    if (!flex) continue;
    if (EXCLUDED_TYPES.includes(flex.type)) continue;

    // Expand Contact-type fields into their individual sub-fields
    if (flex.type === 'Contact' && flex.contactFields) {
      const contactFields = flex.contactFields;
      for (const subFieldName of Object.keys(contactFields)) {
        const subField = contactFields[subFieldName];
        if (subField?.show !== false) {
          result.push({
            name: subFieldName,
            label: camelCaseToLabel(subFieldName),
          });
        }
      }
    } else {
      result.push({ name: fieldName, label: flex.label || fieldName });
    }
  }

  return result;
}
